import React, { useState } from 'react';
import { Plus, Search, Download, FileText, Trash2, Edit2, Eye, Users, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { FONCTION_TYPES, MEMBER_STATUS } from '../../utils/constants';
import { hasPermission, getInitials, getAvatarColor, formatDate, exportMembersToPDF } from '../../utils/helpers';
import Button from '../common/Button';
import Modal from '../common/Modal';

const MembersView = () => {
  const { currentUser } = useAuth();
  const { data, addMember, updateMember, deleteMember } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFonction, setFilterFonction] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [exportOptions, setExportOptions] = useState({
    name: true,
    email: true,
    phone: true,
    address: true,
    dateNaissance: true,
    status: true,
    fonction: true,
    originCity: true,
    mpandray: true
  });
  const [form, setForm] = useState({
    name: '',
    fonction: 'Fachkraft',
    email: '',
    phone: '',
    originCity: '',
    status: 'Actif',
    mpandray: 'Tsia',
    notes: '',
    joinDate: new Date().toISOString().split('T')[0]
  });
  const [errors, setErrors] = useState({});
  const [expandedMember, setExpandedMember] = useState(null);

  const canEdit = hasPermission(currentUser, 'edit_members') || hasPermission(currentUser, 'edit_all');
  const canExport = hasPermission(currentUser, 'export_finances') || hasPermission(currentUser, 'export_all');

  // Filtrer et trier les membres par ordre alphabétique
  const filteredMembers = (data.members?.filter(m => {
    const matchesSearch = m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.fonction?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFonction = filterFonction === 'all' || m.fonction === filterFonction;
    const matchesStatus = filterStatus === 'all' || m.status === filterStatus;
    return matchesSearch && matchesFonction && matchesStatus;
  }) || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Statistiques
  const stats = {
    total: data.members?.length || 0,
    actifs: data.members?.filter(m => m.status === 'Actif').length || 0,
    enAttente: data.members?.filter(m => m.status === 'En attente').length || 0,
    fachkraft: data.members?.filter(m => m.fonction === 'Fachkraft').length || 0,
    azubi: data.members?.filter(m => m.fonction === 'Azubi').length || 0,
    fsjbfd: data.members?.filter(m => m.fonction === 'FSJ/BFD').length || 0,
    aupair: data.members?.filter(m => m.fonction === 'Aupair').length || 0
  };

  // Membres en attente d'approbation
  const pendingMembers = data.members?.filter(m => m.status === 'En attente') || [];

  // Approuver un membre
  const handleApproveMember = async (memberId) => {
    if (window.confirm('Approuver cette demande d\'inscription ?')) {
      await updateMember(memberId, { 
        status: 'Actif',
        approvalStatus: 'approved',
        approvedAt: new Date().toISOString(),
        approvedBy: currentUser.id
      });
    }
  };

  // Rejeter un membre
  const handleRejectMember = async (memberId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    if (reason !== null) {
      await updateMember(memberId, { 
        status: 'Rejeté',
        approvalStatus: 'rejected',
        rejectedAt: new Date().toISOString(),
        rejectedBy: currentUser.id,
        rejectionReason: reason
      });
    }
  };

  const handleOpenAdd = () => {
    setEditingMember(null);
    setForm({
      name: '',
      fonction: 'Fachkraft',
      email: '',
      phone: '',
      originCity: '',
      status: 'Actif',
      mpandray: 'Tsia',
      notes: '',
      joinDate: new Date().toISOString().split('T')[0]
    });
    setErrors({});
    setShowModal(true);
  };

  const handleOpenEdit = (member) => {
    setEditingMember(member);
    setForm({
      name: member.name,
      fonction: member.fonction,
      email: member.email || '',
      phone: member.phone || '',
      originCity: member.originCity || '',
      status: member.status || 'Actif',
      mpandray: member.mpandray || 'Tsia',
      notes: member.notes || '',
      joinDate: member.joinDate ? new Date(member.joinDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setErrors({});
    setShowModal(true);
  };

  const handleViewDetails = (member) => {
    setSelectedMember(member);
    setShowDetailsModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!form.name.trim()) {
      newErrors.name = 'Le nom est obligatoire';
    }
    if (!form.fonction) {
      newErrors.fonction = 'La fonction est obligatoire';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const memberData = {
      name: form.name.trim(),
      fonction: form.fonction,
      email: form.email.trim(),
      phone: form.phone.trim(),
      originCity: form.originCity.trim(),
      status: form.status,
      mpandray: form.mpandray,
      notes: form.notes.trim(),
      joinDate: form.joinDate ? new Date(form.joinDate).toISOString() : new Date().toISOString()
    };

    if (editingMember) {
      updateMember(editingMember.id, memberData);
    } else {
      addMember(memberData);
    }

    setShowModal(false);
    setEditingMember(null);
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
      deleteMember(id);
    }
  };

  const handleExportPDF = () => {
    setShowExportModal(true);
  };

  const handleConfirmExport = () => {
    // Trier les membres par ordre alphabétique
    const sortedMembers = [...(data.members || [])].sort((a, b) => 
      (a.name || '').localeCompare(b.name || '')
    );
    
    exportMembersToPDF(
      sortedMembers, 
      `membres_vomm_${new Date().toISOString().split('T')[0]}`,
      exportOptions
    );
    setShowExportModal(false);
  };

  const toggleExportOption = (field) => {
    setExportOptions(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Membres</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Gestion des membres de l'église</p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <StatCard label="Total" value={stats.total} color="blue" />
        <StatCard label="Actifs" value={stats.actifs} color="green" />
        {stats.enAttente > 0 && (
          <StatCard label="En attente" value={stats.enAttente} color="yellow" />
        )}
        <StatCard label="Fachkraft" value={stats.fachkraft} color="purple" />
        <StatCard label="Azubi" value={stats.azubi} color="cyan" />
        <StatCard label="FSJ/BFD" value={stats.fsjbfd} color="indigo" />
        <StatCard label="Aupair" value={stats.aupair} color="pink" />
      </div>

      {/* Section des demandes en attente (visible uniquement pour Admin et Secrétaire) */}
      {canEdit && pendingMembers.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-yellow-900 dark:text-yellow-200 mb-4 flex items-center gap-2">
            <Users size={20} />
            Demandes d'inscription en attente ({pendingMembers.length})
          </h2>
          <div className="space-y-3">
            {pendingMembers.map(member => (
              <div key={member.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 ${getAvatarColor(member.name)} rounded-full flex items-center justify-center text-white font-bold`}>
                    {getInitials(member.name)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{member.name}</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {member.email} • {member.phone}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      Fonction: {member.fonction} • Demande le {formatDate(member.requestDate)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleApproveMember(member.id)}
                    variant="success"
                    size="sm"
                    icon={CheckCircle}
                  >
                    Approuver
                  </Button>
                  <Button
                    onClick={() => handleRejectMember(member.id)}
                    variant="danger"
                    size="sm"
                    icon={XCircle}
                  >
                    Rejeter
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedMember(member);
                      setShowDetailsModal(true);
                    }}
                    variant="outline"
                    size="sm"
                    icon={Eye}
                  >
                    Détails
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtres et Actions */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* Recherche */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Rechercher un membre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filtre Fonction */}
        <select
          value={filterFonction}
          onChange={(e) => setFilterFonction(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                   focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">Toutes les fonctions</option>
          {FONCTION_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        {/* Filtre Statut */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                   focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">Tous les statuts</option>
          {MEMBER_STATUS.map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>

        {/* Bouton Export PDF */}
        {canExport && (
          <Button
            onClick={handleExportPDF}
            variant="outline"
            icon={FileText}
            size="sm"
            title="Exporter en PDF"
          >
            PDF
          </Button>
        )}

        {/* Bouton Ajouter */}
        {canEdit && (
          <Button
            onClick={handleOpenAdd}
            variant="primary"
            icon={Plus}
          >
            Nouveau Membre
          </Button>
        )}
      </div>

      {/* Table Desktop */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Membre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Fonction
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Téléphone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Statut
              </th>
              {canEdit && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredMembers.map(member => (
              <React.Fragment key={member.id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 ${getAvatarColor(member.name)} rounded-full flex items-center justify-center text-white font-bold mr-3`}>
                        {getInitials(member.name)}
                      </div>
                      <div>
                        <button
                          onClick={() => setExpandedMember(expandedMember === member.id ? null : member.id)}
                          className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer text-left"
                        >
                          {member.name}
                        </button>
                        {member.originCity && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{member.originCity}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 
                                 text-xs font-medium rounded-full">
                      {member.fonction}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                    {member.email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                    {member.phone || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                      member.status === 'Actif' 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : member.status === 'Inactif'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {member.status || 'Actif'}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleOpenEdit(member)}
                          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          title="Modifier"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                {expandedMember === member.id && (
                  <tr>
                    <td colSpan={canEdit ? 6 : 5} className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Email:</span>
                          <p className="text-gray-900 dark:text-white">{member.email || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Téléphone:</span>
                          <p className="text-gray-900 dark:text-white">{member.phone || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Ville d'origine:</span>
                          <p className="text-gray-900 dark:text-white">{member.originCity || 'Non renseignée'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Statut:</span>
                          <p className="text-gray-900 dark:text-white">{member.status || 'Actif'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Mpandray:</span>
                          <p className="text-gray-900 dark:text-white">{member.mpandray || 'Tsia'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Membre depuis:</span>
                          <p className="text-gray-900 dark:text-white">{formatDate(member.joinDate)}</p>
                        </div>
                        {member.notes && (
                          <div className="col-span-2 md:col-span-3">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Notes:</span>
                            <p className="text-gray-900 dark:text-white mt-1 bg-white dark:bg-gray-800 p-3 rounded">
                              {member.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {filteredMembers.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Aucun membre trouvé
          </div>
        )}
      </div>

      {/* Cards Mobile */}
      <div className="md:hidden space-y-4">
        {filteredMembers.map(member => (
          <div key={member.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3 flex-1">
                <div className={`w-12 h-12 ${getAvatarColor(member.name)} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}>
                  {getInitials(member.name)}
                </div>
                <div className="flex-1">
                  <button
                    onClick={() => setExpandedMember(expandedMember === member.id ? null : member.id)}
                    className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-left"
                  >
                    {member.name}
                  </button>
                  <div className="mt-1">
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 
                                   text-xs font-medium rounded-full">
                      {member.fonction}
                    </span>
                  </div>
                </div>
              </div>
              {canEdit && (
                <div className="flex space-x-2 flex-shrink-0">
                  <button
                    onClick={() => handleOpenEdit(member)}
                    className="text-green-600 dark:text-green-400"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="text-red-600 dark:text-red-400"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
            
            {expandedMember === member.id && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-3 text-sm">
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Email:</span>
                  <p className="text-gray-900 dark:text-white">{member.email || 'Non renseigné'}</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Téléphone:</span>
                  <p className="text-gray-900 dark:text-white">{member.phone || 'Non renseigné'}</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Ville d'origine:</span>
                  <p className="text-gray-900 dark:text-white">{member.originCity || 'Non renseignée'}</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Statut:</span>
                  <p className="text-gray-900 dark:text-white">{member.status || 'Actif'}</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Mpandray:</span>
                  <p className="text-gray-900 dark:text-white">{member.mpandray || 'Tsia'}</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Membre depuis:</span>
                  <p className="text-gray-900 dark:text-white">{formatDate(member.joinDate)}</p>
                </div>
                {member.notes && (
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Notes:</span>
                    <p className="text-gray-900 dark:text-white mt-1 bg-gray-50 dark:bg-gray-700 p-3 rounded">
                      {member.notes}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {filteredMembers.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Aucun membre trouvé
          </div>
        )}
      </div>

      {/* Modal Ajout/Édition - Voir fichier séparé MemberFormModal.js */}
      {showModal && (
        <MemberFormModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingMember(null);
          }}
          onSubmit={handleSubmit}
          form={form}
          setForm={setForm}
          errors={errors}
          isEditing={!!editingMember}
        />
      )}

      {/* Modal Détails */}
      {showDetailsModal && selectedMember && (
        <MemberDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedMember(null);
          }}
          member={selectedMember}
          onEdit={() => {
            setShowDetailsModal(false);
            handleOpenEdit(selectedMember);
          }}
          canEdit={canEdit}
        />
      )}

      {/* Modal d'export PDF */}
      {showExportModal && (
        <Modal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Options d'export PDF"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Sélectionnez les informations à inclure dans l'export PDF. Les membres seront triés par ordre alphabétique et un filigrane "Document Confidentiel" sera ajouté.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { field: 'name', label: 'Nom' },
                { field: 'email', label: 'Email' },
                { field: 'phone', label: 'Téléphone' },
                { field: 'address', label: 'Adresse' },
                { field: 'dateNaissance', label: 'Date de naissance' },
                { field: 'originCity', label: "Ville d'origine" },
                { field: 'fonction', label: 'Fonction' },
                { field: 'status', label: 'Statut' },
                { field: 'mpandray', label: 'Mpandray' }
              ].map(({ field, label }) => (
                <label key={field} className="flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                  <input
                    type="checkbox"
                    checked={exportOptions[field]}
                    onChange={() => toggleExportOption(field)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <Button onClick={() => setShowExportModal(false)} variant="outline">
                Annuler
              </Button>
              <Button onClick={handleConfirmExport} variant="primary" icon={FileText}>
                Exporter PDF
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Composant Carte de statistique
const StatCard = ({ label, value, color }) => {
  const colors = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
    cyan: 'bg-cyan-500',
    indigo: 'bg-indigo-500',
    pink: 'bg-pink-500'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
      <div className={`w-12 h-12 ${colors[color]} rounded-full flex items-center justify-center mx-auto mb-2`}>
        <Users className="text-white" size={24} />
      </div>
      <p className="text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{label}</p>
    </div>
  );
};

// Modal Formulaire (simplifié - à extraire dans un fichier séparé)
const MemberFormModal = ({ isOpen, onClose, onSubmit, form, setForm, errors, isEditing }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={isEditing ? 'Modifier le membre' : 'Nouveau membre'}
  >
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Nom et Prénoms *
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={`w-full border rounded-lg px-4 py-2 bg-white dark:bg-gray-800 
                   text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent
                   ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          placeholder="Ex: Jean Dupont"
        />
        {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Fonction *
        </label>
        <select
          value={form.fonction}
          onChange={(e) => setForm({ ...form, fonction: e.target.value })}
          className={`w-full border rounded-lg px-4 py-2 bg-white dark:bg-gray-800 
                   text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent
                   ${errors.fonction ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
        >
          {FONCTION_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        {errors.fonction && <p className="text-red-600 text-sm mt-1">{errors.fonction}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="email@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Téléphone
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="+49 151 123 456"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ville d'origine
          </label>
          <input
            type="text"
            value={form.originCity}
            onChange={(e) => setForm({ ...form, originCity: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Ex: Antananarivo"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Statut
          </label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {MEMBER_STATUS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mpandray
          </label>
          <select
            value={form.mpandray}
            onChange={(e) => setForm({ ...form, mpandray: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Eny">Eny</option>
            <option value="Tsia">Tsia</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Membre depuis
          </label>
          <input
            type="date"
            value={form.joinDate}
            onChange={(e) => setForm({ ...form, joinDate: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Notes
        </label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 h-20
                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                   focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Informations complémentaires..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" onClick={onClose} variant="outline">
          Annuler
        </Button>
        <Button type="submit" variant="primary">
          {isEditing ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </div>
    </form>
  </Modal>
);

// Modal Détails
const MemberDetailsModal = ({ isOpen, onClose, member, onEdit, canEdit }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title="Détails du membre"
  >
    <div className="space-y-6">
      <div className="flex items-center space-x-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className={`w-16 h-16 ${getAvatarColor(member.name)} rounded-full flex items-center justify-center text-white font-bold text-xl`}>
          {getInitials(member.name)}
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-white">{member.name}</h3>
          <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 
                         text-xs font-medium rounded-full">
            {member.fonction}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <InfoRow label="Email" value={member.email || 'Non renseigné'} />
        <InfoRow label="Téléphone" value={member.phone || 'Non renseigné'} />
        <InfoRow label="Ville d'origine" value={member.originCity || 'Non renseignée'} />
        <InfoRow label="Statut" value={member.status || 'Actif'} />
        <InfoRow label="Mpandray" value={member.mpandray || 'Tsia'} />
        <InfoRow label="Membre depuis" value={formatDate(member.joinDate)} />
        {member.notes && (
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Notes:</p>
            <p className="text-sm text-gray-800 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded">
              {member.notes}
            </p>
          </div>
        )}
      </div>

      {canEdit && (
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button onClick={onClose} variant="outline">
            Fermer
          </Button>
          <Button onClick={onEdit} variant="primary" icon={Edit2}>
            Modifier
          </Button>
        </div>
      )}
    </div>
  </Modal>
);

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-600">
    <span className="text-gray-600 dark:text-gray-400">{label}</span>
    <span className="font-medium text-gray-800 dark:text-white">{value}</span>
  </div>
);

export default MembersView;
